import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Formik, Field } from 'formik';
import { FieldCustom, FieldToggle, FieldMarkdown } from 'components/molecules';
import { Form, Button } from 'components/atoms';
import { phoneRegex } from 'services/constants';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

const markdownTools = ['inline', 'link', 'list', 'history']

export const Schema = Yup.object().shape({
  visible: Yup.boolean().nullable(),
  cabinet: Yup.string().nullable(),
  externalPhone: Yup.string().matches(phoneRegex, 'Número inválido').nullable(),
  internalPhone: Yup.string().matches(phoneRegex, 'Número inválido').nullable(),
  jobPt: Yup.string().nullable()
    .max(2000, 'Máximo de 2000 caracteres'),
  jobEn: Yup.string().nullable()
    .max(2000, 'Máximo de 2000 caracteres'),
});

const PersonUpdateForm = ({
  initialValues, subweb, peopleId, onSubmit, isLoading,
}) => {
  const isInitialValid = useMemo(
    () => Schema.isValidSync(initialValues),
    [initialValues]
  );

  const handleSubmit = async values => {
    const {
      cabinet,
      externalPhone,
      internalPhone,
      jobPt,
      jobEn,
      visible,
    } = values

    onSubmit({
      subweb,
      id: peopleId,
      cabinet,
      externalPhone,
      internalPhone,
      jobPt,
      jobEn,
      visible,
    })
  };

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={isInitialValid}
      validationSchema={Schema}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
    >
      {({ isSubmitting, resetForm, dirty }) => (
        <Form>
          <Field
            title="Visível no Portal"
            name="visible"
            component={FieldToggle}
            modifiers={['stretch']}
          />

          <Field
            title="Gabinete"
            name="cabinet"
            component={FieldCustom}
          />

          <Field
            title="Telefone externo"
            name="externalPhone"
            component={FieldCustom}
          />

          <Field
            title="Extensão telefónica"
            name="internalPhone"
            component={FieldCustom}
          />

          <Field
            title="Atribuições funcionais PT"
            name="jobPt"
            component={FieldMarkdown}
            info="atribuições funcionais em português"
            toolbar={markdownTools}
            maxLetters={2000}
          />

          <Field
            title="Atribuições funcionais EN"
            name="jobEn"
            component={FieldMarkdown}
            info="atribuições funcionais em inglês"
            toolbar={markdownTools}
            maxLetters={2000}
          />

          <Actions>
            <Button
              type="button"
              modifiers={['marginRight', 'capitalize', dirty ? '' : 'disabled']}
              onClick={() => resetForm(initialValues)}
            >
              Repor
            </Button>
            <Button
              type="submit"
              modifiers={['dark', 'capitalize', dirty ? '' : 'disabled']}
              loading={isSubmitting || isLoading}
            >
              Guardar alterações
            </Button>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
`

PersonUpdateForm.propTypes = {
  onSubmit: PropTypes.func,
  initialValues: PropTypes.shape({
    visible: PropTypes.bool,
    cabinet: PropTypes.string,
    externalPhone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    internalPhone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    jobPt: PropTypes.string,
    jobEn: PropTypes.string,
  }),
  subweb: PropTypes.string,
  peopleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
};

PersonUpdateForm.defaultProps = {
  onSubmit: () => null,
  initialValues: {
    title: '',
  },
  subweb: null,
  peopleId: null,
  isLoading: false,
};

export default PersonUpdateForm;
